import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  validateAuth0Config,
  type Auth0Config,
  type Auth0ConfigValidationResult,
} from '@/config/auth0';
import {
  peekStoredReturnPath,
  clearReturnPath,
  rememberReturnPath,
} from '@/lib/authReturnPath';
import { queryClient } from '@/lib/queryClient';
import { clearUnclaimedClientCache } from '@/hooks/useUnclaimed';
import { redirectTo } from '@/lib/navigation';
import {
  isLocalAuthBypassEnabled,
  LOCAL_AUTH_BYPASS_ROLE_OPTIONS,
} from '@/config/local-auth-bypass';
import type { LocalAuthBypassRole } from '@/config/local-auth-bypass';

export interface User {
  id: number;
  email: string;
  displayName: string;
  role: string;
  subscriptionTier: string;
  advancedMode: boolean;
  defaultProvider: string | null;
  defaultModel: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  agencyId?: string | null;
  teamId?: number | null;
  teamMemberCount?: number | null;
  managedTeamIds?: number[] | null;
  financesUnclaimedAccess?: boolean;
}

export interface TramsContext {
  tramsAccountId: number;
  profileId: number;
  agentName: string;
  interfaceId: string;
}

interface AuthContextType {
  user: User | null;
  tramsContext: TramsContext | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mode: 'auth0' | 'local-bypass';
  localBypassEnabled: boolean;
  availableBypassRoles: readonly LocalAuthBypassRole[];
  login: (email?: string, password?: string) => Promise<void>;
  localLogin: (input: LocalLoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  register: (
    email?: string,
    displayName?: string,
    password?: string,
    confirmPassword?: string,
  ) => Promise<void>;
}

export interface LocalLoginRequest {
  email: string;
  role: LocalAuthBypassRole;
  displayName?: string;
  agencyId?: string | null;
  teamId?: number | null;
  managedTeamIds?: number[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Safe for hooks that may run before AuthProvider is ready (e.g. feature-flag gates). */
export const useAuthOptional = (): AuthContextType | undefined =>
  useContext(AuthContext);

interface AuthProviderOverrides {
  validation?: Auth0ConfigValidationResult;
  redirect?: (url: string) => void;
  fetchImpl?: typeof fetch;
  logoutRedirect?: (url: string) => void;
  localBypassEnabled?: boolean;
}

const determineReturnTo = (): string => {
  const stored = peekStoredReturnPath();
  if (stored) {
    return stored;
  }

  if (typeof window === 'undefined' || !window.location) {
    return '/threads';
  }

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (
    !path ||
    path === '/' ||
    path.startsWith('/login') ||
    path.startsWith('/register')
  ) {
    return '/threads';
  }

  return path;
};

const startAuthorization = async (
  config: Auth0Config,
  fetchImpl: typeof fetch,
  screenHint: 'login' | 'signup' | undefined,
): Promise<string> => {
  const params = new URLSearchParams();
  params.set('returnTo', determineReturnTo());
  if (screenHint) {
    params.set('screen', screenHint);
  }

  const response = await fetchImpl(`/api/auth/auth0/authorize?${params.toString()}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    let message = `Failed to initiate Auth0 login (${response.status})`;
    try {
      const body = await response.json();
      if (typeof body?.error === 'string') {
        message = body.error;
      }
    } catch {
      // swallow JSON parse errors
    }
    throw new Error(message);
  }

  const body = (await response.json()) as { authorizationUrl?: string };
  if (!body.authorizationUrl) {
    throw new Error('Auth0 authorization URL was not provided by the server.');
  }

  return body.authorizationUrl;
};

export const AuthProvider: React.FC<{
  children: React.ReactNode;
  overrides?: AuthProviderOverrides;
}> = ({ children, overrides }) => {
  const { toast } = useToast();
  const validation = useMemo(() => {
    if (overrides?.validation) {
      return overrides.validation;
    }
    return validateAuth0Config();
  }, [overrides?.validation]);
  const config = validation.config;
  const redirectFn = overrides?.redirect ?? redirectTo;
  const fetchFn = overrides?.fetchImpl ?? fetch;
  const localBypassEnabled =
    overrides?.localBypassEnabled ?? isLocalAuthBypassEnabled();

  const [user, setUser] = useState<User | null>(null);
  const prevUserIdRef = useRef<number | null>(null);
  const [tramsContext, setTramsContext] = useState<TramsContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState<'auth0' | 'local-bypass'>(
    localBypassEnabled ? 'local-bypass' : 'auth0',
  );
  const hasRequestedProfile = useRef(false);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await fetchFn('/api/auth/me', {
        credentials: 'include',
      });

      if (!response.ok) {
        setUser(null);
        setAuthMode(localBypassEnabled ? 'local-bypass' : 'auth0');
        return;
      }

      const body = await response.json();
      setUser(body.user);
      setTramsContext(body.tramsContext ?? null);
      setAuthMode(localBypassEnabled ? 'local-bypass' : 'auth0');
      clearReturnPath();
    } catch {
      setUser(null);
      setTramsContext(null);
      setAuthMode(localBypassEnabled ? 'local-bypass' : 'auth0');
    }
  }, [fetchFn, localBypassEnabled]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (hasRequestedProfile.current) {
        return;
      }
      hasRequestedProfile.current = true;
      setIsLoading(true);
      try {
        if (config || localBypassEnabled) {
          await fetchCurrentUser();
        } else {
          toast({
            title: 'Auth0 configuration incomplete',
            description:
              'Missing Auth0 environment variables. Please update the deployment configuration.',
            variant: 'destructive',
          });
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [config, fetchCurrentUser, localBypassEnabled, toast]);

  // Local bypass can switch accounts without logout — drop stale unclaimed cache.
  useEffect(() => {
    const nextId = user?.id ?? null;
    const prevId = prevUserIdRef.current;
    if (prevId != null && nextId != null && prevId !== nextId) {
      clearUnclaimedClientCache(queryClient);
    }
    prevUserIdRef.current = nextId;
  }, [user?.id]);

  const login = useCallback(
    async (_email?: string, _password?: string) => {
      if (!config) {
        throw new Error('Auth0 configuration is not available.');
      }
      setIsLoading(true);
      try {
        rememberReturnPath();
        const url = await startAuthorization(config, fetchFn, 'login');
        redirectFn(url);
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    [config, fetchFn, redirectFn],
  );

  const localLogin = useCallback(
    async ({
      email,
      role,
      displayName,
      agencyId,
      teamId,
      managedTeamIds,
    }: LocalLoginRequest) => {
      if (!localBypassEnabled) {
        throw new Error('Local auth bypass is not enabled.');
      }
      setIsLoading(true);
      try {
        const body: Record<string, unknown> = {
          email,
          role,
        };

        if (displayName) {
          body.displayName = displayName;
        }

        if (agencyId !== undefined) {
          body.agencyId = agencyId;
        }

        if (teamId !== undefined) {
          body.teamId = teamId;
        }

        if (managedTeamIds !== undefined) {
          body.managedTeamIds = managedTeamIds;
        }

        const response = await fetchFn('/api/auth/local-login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          let message = 'Local login failed.';
          try {
            const data = await response.json();
            if (typeof data?.message === 'string') {
              message = data.message;
            } else if (typeof data?.error === 'string') {
              message = data.error;
            }
          } catch {
            // ignore JSON parse errors
          }
          throw new Error(message);
        }

        const responseBody = (await response.json()) as {
          user: User | null | undefined;
        };
        setUser(responseBody.user ?? null);
        setAuthMode('local-bypass');
        clearReturnPath();
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFn, localBypassEnabled],
  );

  const register = useCallback(
    async () => {
      if (!config) {
        throw new Error('Auth0 configuration is not available.');
      }
      setIsLoading(true);
      try {
        rememberReturnPath();
        const url = await startAuthorization(config, fetchFn, 'signup');
        redirectFn(url);
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    [config, fetchFn, redirectFn],
  );

  const logoutRedirectFn = overrides?.logoutRedirect ?? ((url: string) => {
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
  });

  const logout = useCallback(async () => {
    // Clear legacy chat import flag so it re-checks on next login
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('aivia_legacy_chat_checked');
    }
    
    clearUnclaimedClientCache(queryClient);
    queryClient.clear();

    if (authMode === 'local-bypass') {
      setIsLoading(true);
      try {
        await fetchFn('/api/auth/local-logout', {
          method: 'POST',
          credentials: 'include',
        });
      } finally {
        setUser(null);
        setTramsContext(null);
        clearReturnPath();
        setAuthMode(localBypassEnabled ? 'local-bypass' : 'auth0');
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    setUser(null);
    setTramsContext(null);
    logoutRedirectFn('/api/auth/logout');
  }, [authMode, fetchFn, localBypassEnabled, logoutRedirectFn]);

  const value: AuthContextType = {
    user,
    tramsContext,
    isAuthenticated: !!user,
    isLoading,
    mode: authMode,
    localBypassEnabled,
    availableBypassRoles: LOCAL_AUTH_BYPASS_ROLE_OPTIONS,
    login,
    localLogin,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
